/**
 * QuizSlide - Individual Question Slide Component
 * 
 * Displays a single quiz question with options or text input.
 * Supports single/multiple selection and text entry.
 *
 * UX Update v2: every selection launches a botanical burst from the clicked
 * card. Answer submission adds a 2.5-second top-slot acknowledgment before the
 * quiz advances, so both the celebration and reading-counter tick are seen.
 */

import React, { useState, useEffect, useRef } from 'react';
import { QuizQuestion, QuizState } from '../../utils/citrusQuizLogic';
import OptionCard from './OptionCard';
import {
  fireQuizBotanicalBurst,
  QUIZ_ACK_DURATION_MS,
  type QuizBurstKind,
} from '../../utils/quizConfetti';

/**
 * Why this matters: every message gets a predictable 2.5-second reading
 * window before the quiz advances.
 */
const CITRUS_MILESTONE_QUESTIONS = new Set([
  'experience',
  'flavor',
  'vibe',
  'bonus-features',
]);

function singleSelectBurst(questionId: string): QuizBurstKind {
  if (questionId === 'identity') return 'final-answer';
  return CITRUS_MILESTONE_QUESTIONS.has(questionId) ? 'milestone' : 'option';
}

function submitBurst(questionId: string): QuizBurstKind {
  return CITRUS_MILESTONE_QUESTIONS.has(questionId) ? 'milestone' : 'submit';
}

interface QuizSlideProps {
  question: QuizQuestion;
  state: QuizState;
  onAnswer: (selectedOptions: string[], textValue?: string) => void;
  onContinue: () => void;
  onBack?: (() => void) | undefined;
  canContinue: boolean;
  /**
   * Called once per answer submission. Returns the celebration line to show
   * and lets the orchestrator tick the reading counter at the same moment.
   */
  onAcknowledge?: (() => string) | undefined;
  /** Lift the acknowledgment into the shared top-of-quiz message slot. */
  onAcknowledgmentChange?: ((line: string | null) => void) | undefined;
}

const QuizSlide: React.FC<QuizSlideProps> = ({
  question,
  state,
  onAnswer,
  onContinue,
  onBack,
  canContinue,
  onAcknowledge,
  onAcknowledgmentChange,
}) => {
  // Get current selections from state
  const currentAnswer = state.answers.find(a => a.questionId === question.id);
  const [selectedOptions, setSelectedOptions] = useState<string[]>(
    currentAnswer?.selectedOptions || []
  );
  const ackTimer = useRef<number | null>(null);
  const [isAcknowledging, setIsAcknowledging] = useState(false);
  const [textValue, setTextValue] = useState<string>(() => {
    if (question.id === 'welcome') return state.userName;
    if (question.id === 'zipcode') return state.zipCode;
    if (question.id === 'email') return state.email;
    return '';
  });

  useEffect(
    () => {
      // Why this matters: a question change can cancel the old timer, so the
      // new question must not inherit its disabled interaction state.
      setIsAcknowledging(false);
      onAcknowledgmentChange?.(null);
      return () => {
        if (ackTimer.current !== null) {
          window.clearTimeout(ackTimer.current);
          ackTimer.current = null;
        }
        onAcknowledgmentChange?.(null);
      };
    },
    [question.id, onAcknowledgmentChange]
  );

  // Update selections when answer changes
  useEffect(() => {
    setSelectedOptions(currentAnswer?.selectedOptions || []);
  }, [currentAnswer]);

  // Reset text value when question changes
  useEffect(() => {
    if (question.id === 'welcome') {
      setTextValue(state.userName);
    } else if (question.id === 'zipcode') {
      setTextValue(state.zipCode);
    } else if (question.id === 'email') {
      setTextValue(state.email);
    } else {
      setTextValue('');
    }
  }, [question.id, state.userName, state.zipCode, state.email]);

  /**
   * UX-1: show the micro-acknowledgment, then advance. The timer ref locks
   * synchronously while state disables the controls on the next render, so
   * rapid taps can never advance twice off one answer.
   */
  const beginAcknowledgment = (source: HTMLElement | null, burstKind: QuizBurstKind) => {
    if (ackTimer.current !== null || isAcknowledging) return;
    fireQuizBotanicalBurst(source, 'uscn', burstKind);
    const line = onAcknowledge ? onAcknowledge() : '';
    setIsAcknowledging(true);
    onAcknowledgmentChange?.(line);
    ackTimer.current = window.setTimeout(() => {
      ackTimer.current = null;
      setIsAcknowledging(false);
      // Why this matters: clear the shared slot in the same update as navigation
      // so the previous answer cannot flash beside the next question.
      onAcknowledgmentChange?.(null);
      onContinue();
    }, QUIZ_ACK_DURATION_MS);
  };

  // Handle option selection
  const handleOptionSelect = (optionId: string, source: HTMLButtonElement) => {
    if (ackTimer.current !== null || isAcknowledging) return;
    let newSelections: string[];
    const exclusiveOptionIds = question.exclusiveOptionIds || [];
    const wasSelected = selectedOptions.includes(optionId);
    
    if (question.allowMultiple) {
      const isExclusive = exclusiveOptionIds.includes(optionId);

      // Toggle selection for multi-select
      if (selectedOptions.includes(optionId)) {
        newSelections = selectedOptions.filter(id => id !== optionId);
      } else {
        if (isExclusive) {
          newSelections = [optionId];
        } else {
          const nonExclusiveSelections = selectedOptions.filter(
            (id) => !exclusiveOptionIds.includes(id)
          );

          // Check max selections
          if (question.maxSelections && nonExclusiveSelections.length >= question.maxSelections) {
            // Remove first selection and add new one
            newSelections = [...nonExclusiveSelections.slice(1), optionId];
          } else {
            newSelections = [...nonExclusiveSelections, optionId];
          }
        }
      }
    } else {
      // Single select - replace selection
      newSelections = [optionId];
    }
    
    setSelectedOptions(newSelections);
    onAnswer(newSelections);

    // Multi-select choices get a tiny local reward without pausing the flow.
    // Single-selects use the larger submission burst and acknowledgment.
    if (question.allowMultiple && !wasSelected) {
      fireQuizBotanicalBurst(source, 'uscn', 'multi-option');
    }
    if (!question.allowMultiple && question.autoAdvance) {
      beginAcknowledgment(source, singleSelectBurst(question.id));
    }
  };

  /**
   * Continue submits the answer: option questions get the acknowledgment
   * first; text steps (email and friends) move on immediately.
   */
  const handleContinueClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (ackTimer.current !== null || isAcknowledging) return;
    if (question.type === 'single' || question.type === 'multiple') {
      beginAcknowledgment(event.currentTarget, submitBurst(question.id));
      return;
    }
    onContinue();
  };

  const handleBackClick = onBack
    ? () => {
        if (ackTimer.current !== null || isAcknowledging) return;
        onBack();
      }
    : undefined;

  // Handle text input change
  // Why this matters: Validates input based on question type - zipcode only allows numbers to prevent invalid entries
  const handleTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value;
    
    // For zipcode, only allow numeric characters
    if (question.id === 'zipcode') {
      value = value.replace(/\D/g, ''); // Remove all non-digit characters
    }
    
    setTextValue(value);
    onAnswer([], value);
  };

  // Handle Enter key on text input
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && canContinue) {
      onContinue();
    }
  };

  // Helper to interpolate {{name}} placeholder
  const personalize = (text: string) => {
    if (!text) return text;
    return text.replace(/\{\{name\}\}/g, state.userName || 'friend');
  };

  return (
    <div className="quiz-slide">
      {/* Question Header — eyebrow → lede (subtitle) → title so intro copy sits above Q1 */}
      <div className="quiz-slide-header">
        {question.eyebrow && (
          <p className="quiz-slide-eyebrow">{personalize(question.eyebrow)}</p>
        )}
        {question.subtitle && (
          <p className="quiz-slide-subtitle">{personalize(question.subtitle)}</p>
        )}
        <h1
          className="quiz-slide-title"
          id={question.allowMultiple ? `quiz-question-${question.id}` : undefined}
        >
          {personalize(question.title)}
        </h1>
        {question.helperText && (
          <p className="quiz-slide-helper">{personalize(question.helperText)}</p>
        )}
        {question.allowMultiple && (
          <span className='instruction'><b>Choose as many as you like</b></span>
        )}
      </div>
      {/* Question Content */}
      <div className="quiz-slide-content">
        {question.type === 'text' ? (
          <div className="quiz-text-input-container">
            <div className="quiz-text-input-wrapper">
              {question.id === 'welcome' && (
                <span className="quiz-text-prefix">Hi, my name is</span>
              )}
              <input
                type={question.id === 'email' ? 'email' : 'text'}
                value={textValue}
                onChange={handleTextChange}
                onKeyDown={handleKeyDown}
                placeholder={question.placeholder}
                className="quiz-text-input"
                maxLength={question.id === 'zipcode' ? 5 : undefined}
                pattern={question.id === 'zipcode' ? '[0-9]*' : undefined}
                inputMode={question.id === 'zipcode' ? 'numeric' : undefined}
                autoFocus
              />
              {question.id === 'welcome' && (
                <span className="quiz-text-suffix">.</span>
              )}
            </div>
          </div>
        ) : (
          <div
            className={`quiz-options-grid ${
              question.options && (question.options.length === 3 || question.options.length === 6)
                ? 'quiz-options-grid-3'
                : question.options && (question.options.length === 2 || question.options.length === 4 || question.options.length > 6)
                  ? 'quiz-options-grid-2'
                  : 'quiz-options-grid-1'
            }`}
            role={question.allowMultiple ? 'group' : undefined}
            aria-labelledby={question.allowMultiple ? `quiz-question-${question.id}` : undefined}
          >
            {question.options?.map((option) => (
              <OptionCard
                key={option.id}
                option={option}
                isSelected={selectedOptions.includes(option.id)}
                onSelect={(source) => handleOptionSelect(option.id, source)}
                isMultiple={question.allowMultiple}
                showEmoji={true}
              />
            ))}
          </div>
        )}
      </div>

      {/* Navigation Buttons — no Continue on auto-advancing single-selects (UX-3). */}
      <div className="quiz-slide-navigation">
        {!question.autoAdvance && (
          <button
            className="quiz-btn quiz-btn-primary"
            onClick={handleContinueClick}
            disabled={!canContinue || isAcknowledging}
          >
            {question.buttonLabel || 'Continue'}
          </button>
        )}
        {handleBackClick && (
          <button
            className="quiz-btn quiz-btn-back"
            onClick={handleBackClick}
          >
            ← Back
          </button>
        )}
      </div>
    </div>
  );
};

export default QuizSlide;
